import { PrismaEnums } from '@airreading/schema/browser';
import { z } from 'zod';

import { PostCoachUsersCSVFormKeys as coachCsvFormKeys } from './data/coach-csv.js';
import { studentYearInputSchema } from './data/index.js';
import { studentTagSchema } from './data/student.js';

/**
 * Helper to transform string to boolean: yes -> true and no -> false
 */
export const booleanFromYesNo = z.union([
  z.literal('yes').transform(() => true),
  z.literal('no').transform(() => false),
]);

/**
 * This module is now deprecated, as newer data types fall under data service
 * conventions and live in `packages/common/src/data`. Avoid adding more items
 * in this module.
 */

export const putSchedulesStatusBody = z.object({
  status: z.nativeEnum(PrismaEnums.ScheduleStatus),
  scheduleIds: z.string().array(),
});
export type PutSchedulesStatusBody = z.infer<typeof putSchedulesStatusBody>;

export const getSchedulesByIdsBody = z.object({
  scheduleIds: z.array(z.string()),
});

const fleidCSVSchema = z
  .string()
  .trim()
  .nullable()
  .optional()
  .or(z.literal(''))
  .refine((val) => !val || val.trim() === '' || /^[Ff][Ll]\d{12}$/.test(val.trim()), {
    message: 'Unrecognized FLEID format',
  })
  .transform((val): string | null => {
    if (!val || val.trim() === '') return null;
    return val.trim().toUpperCase();
  });

const studentTagCSVSchema = z
  .union([z.string(), z.literal(''), z.null(), z.undefined()])
  .transform((val) => {
    if (val === null || val === undefined || typeof val !== 'string' || val.trim() === '') {
      return null;
    }

    const tags: Array<z.infer<typeof studentTagSchema>> = [];
    const seen = new Set<z.infer<typeof studentTagSchema>>();
    for (const part of val.split(',')) {
      const parsed = studentTagSchema.safeParse(part.trim().toLowerCase());
      if (parsed.success && !seen.has(parsed.data)) {
        seen.add(parsed.data);
        tags.push(parsed.data);
      }
    }

    return tags.length > 0 ? tags : null;
  })
  .pipe(z.array(studentTagSchema).nullable());

export const postStudentUserCSVRow = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z
      .union([z.string(), z.null(), z.undefined()])
      .transform((val): string | null => {
        if (val == null) return null;
        const trimmed = val.trim();
        if (trimmed === '') return null;
        return trimmed.toLowerCase();
      })
      .pipe(z.string().email('Invalid email').nullable()),
    grade: studentYearInputSchema,
    specialEd: booleanFromYesNo.nullable(),
    dyslexic: booleanFromYesNo.nullable(),
    ell: booleanFromYesNo.nullable(),
    ['fleid (optional)']: fleidCSVSchema.nullable(),
    ['Classroom teacher (optional)']: z.string().nullable(),
    ['studentTag (optional)']: studentTagCSVSchema.nullable(),
    ['schedule (optional)']: z.string().nullable(),
    ['classTimeCT (optional)']: z.string().nullable(),
    ['School parent email (optional)']: z.string().max(0).nullable().or(z.string().email('Invalid parent email')),
    ['School parent phone number (optional)']: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.email === null && !data['fleid (optional)']) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FLEID is required for users without email',
        path: ['fleid (optional)'],
      });
    }
  });

export const PostStudentUsersCSVFormKeys = {
  studentsCSV: 'studentsCSV',
  schoolId: 'schoolId',
} as const;

export const postStudentUsersCSVBody = z.object({
  [PostStudentUsersCSVFormKeys.schoolId]: z.string({ message: 'School ID is required' }),
});

export const PostCoachUsersCSVFormKeys = coachCsvFormKeys;

/**
 * Roles supported by the test authentication endpoint (non-production only).
 * Used by POST /me/test-auth and automation tests.
 */
export const testAuthRoleSchema = z.enum(['admin', 'teacher', 'student']);
export type TestAuthRole = z.infer<typeof testAuthRoleSchema>;
export const TestAuthRole = testAuthRoleSchema.enum;

/**
 * Schema for test authentication endpoint (non-production only)
 * Allows generating Firebase custom tokens for test users
 */
export const testAuthRequestSchema = z
  .object({
    email: z.string().email('Invalid email').optional(),
    role: testAuthRoleSchema.optional(),
  })
  .refine((data) => data.email || data.role, {
    message: 'Either email or role must be provided',
  });
export type TestAuthRequest = z.infer<typeof testAuthRequestSchema>;

const mmddyyyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;

export const parseMMDDYYYY = (s: string): Date | null => {
  if (!mmddyyyyRegex.test(s)) return null;
  const [mm, dd, yyyy] = s.split('/').map(Number);
  const d = new Date(yyyy, mm - 1, dd);
  return d.getMonth() === mm - 1 && d.getDate() === dd ? d : null;
};

export const formatDateToISO = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const postCoachPaymentCSVRow = z.object({
  email: z.string(),
  ['pay_cycle']: z.string({ required_error: 'Missing pay period' }).trim().min(1, 'Missing pay period'),
  ['pay_date']: z.string({ required_error: 'Missing pay date' }).trim().min(1, 'Missing pay date'),
  ['payment_type']: z.string().trim().min(1, 'Missing payment type'),
  amount: z.preprocess(
    (val) => {
      if (typeof val !== 'string') return val;
      const normalized = val.trim().replaceAll('$', '').replaceAll(',', '');
      if (normalized === '') return Number.NaN;
      return Number(normalized);
    },
    z
      .number({ invalid_type_error: 'Amount must be a valid number' })
      .finite('Amount must be a valid number')
      .min(0.01, 'Amount must be at least 0.01'),
  ),
});

export const PostCoachPaymentCSVFormKeys = {
  paymentsCSV: 'paymentsCSV',
} as const;
